import {
  type Document,
  isSlicesContent,
  SlicesContent,
} from "@prismicio/types-internal/lib/content"
import type {
  StaticCustomType,
  StaticSection,
  StaticSlices,
  StaticWidget,
} from "@prismicio/types-internal/lib/customtypes"

import type { SliceSelection } from "./SliceSelection"
import { SliceZoneEditor } from "./zones/SliceZoneEditor"
import type { StaticZone, StaticZoneContent } from "./zones/StaticZone"
import { StaticZoneEditor } from "./zones/StaticZoneEditor"

interface Props {
  id: string
  readOnly: boolean
  selection?: SliceSelection
  section: StaticSection
  content: Document
  onContentChange: (content: Document) => void
  documentId: string
  documentContent: Document
  documentCustomType: StaticCustomType
  documentLocale: string
}

export function SectionEditor(props: Props) {
  const {
    id,
    readOnly,
    section,
    content,
    onContentChange,
    documentId,
    documentContent,
    documentCustomType,
    documentLocale,
  } = props

  const [staticZone, slices] = useSplitSection(section)
  const [staticZoneContent, slicesContent] = useSplitSectionContent(content)

  // TODO: Refactor this to a Box once we have healthy zIndex management:
  // https://linear.app/prismic/issue/PBD-1574/sanitize-z-index-management
  // Here we create a new stacking context so that none of the field constituents (if direct children) can be rendered above
  // something outside the section editor.
  // In particular, the Group's dropIndicators need z-index: 1.
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        position: "relative",
        zIndex: 0,
      }}
      data-demo-id="section-editor"
    >
      <StaticZoneEditor
        id={id}
        readOnly={readOnly}
        staticZone={staticZone}
        documentId={documentId}
        content={staticZoneContent}
        onContentChange={(newStaticZoneContent) =>
          onContentChange({ ...slicesContent, ...newStaticZoneContent })
        }
        documentContent={documentContent}
        documentCustomType={documentCustomType}
        documentLocale={documentLocale}
      />
      {slices.map(([sliceZoneId, sliceZone]) => {
        const sliceContent = slicesContent[sliceZoneId]

        return (
          <SliceZoneEditor
            key={sliceZoneId}
            id={`${id}.${sliceZoneId}`}
            readOnly={readOnly}
            sliceZone={sliceZone}
            content={sliceContent}
            onContentChange={(newSliceContent) =>
              onContentChange({
                ...staticZoneContent,
                ...slicesContent,
                [sliceZoneId]: newSliceContent,
              })
            }
          />
        )
      })}
    </div>
  )
}

type SliceZones = [string, StaticSlices][]
type SliceZonesContent = Record<string, SlicesContent>

function isSliceZone(widget: StaticWidget): widget is StaticSlices {
  return widget.type === "Choice" || widget.type === "Slices"
}

function useSplitSection(section: StaticSection): [StaticZone, SliceZones] {
  return Object.entries(section).reduce<[StaticZone, SliceZones]>(
    ([staticZone, sliceZones], [widgetId, widget]) => {
      if (isSliceZone(widget)) {
        sliceZones.push([widgetId, widget])
      } else {
        Object.assign(staticZone, { [widgetId]: widget })
      }
      return [staticZone, sliceZones]
    },
    [{}, []],
  )
}

// Note: There's no such thing as a static zone per se: it's just the accumulation
// of all top level widgets (i.e widgets that are not part of a slice zone)
function useSplitSectionContent(
  content: Document,
): [StaticZoneContent, SliceZonesContent] {
  return Object.entries(content).reduce(
    ([staticZoneContent, sliceZonesContent], [widgetId, widgetContent]) => {
      const entry = { [widgetId]: widgetContent }

      Object.assign(
        isSlicesContent(widgetContent) ? sliceZonesContent : staticZoneContent,
        entry,
      )

      return [staticZoneContent, sliceZonesContent]
    },
    [{}, {}],
  )
}
